/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getTopLevelDomain, isSetLangSupported } from "@wf-components/common-components-consumer";

/**
 * Appends a `setlang` query parameter to an absolute cross-domain href so that
 * the target site can pick up the locale from the URL and set its own cookie.
 *
 * The param is added only when ALL of the following are true:
 *  - `locale` is non-empty and present in the `locales` allowlist
 *  - `href` is an absolute http/https URL
 *  - The href's top-level domain differs from `currentHostname`'s top-level domain
 *
 * @param href            - The link destination
 * @param locale          - The active locale string (e.g. "de", "en-US")
 * @param locales         - Allowlist of valid locale codes
 * @param currentHostname - Hostname of the current page (e.g. `window.location.hostname`)
 * @returns The href unchanged, or with `?setlang=<locale>` appended/merged
 *
 * @example
 * appendSetLangParam(
 *   "https://www.unrealengine.com/en-US/download",
 *   "de",
 *   ["en-US", "de"],
 *   "www.epicgames.com",
 * )
 * // => "https://www.unrealengine.com/en-US/download?setlang=de"
 */
export const appendSetLangParam = (
	href: string,
	locale: string,
	locales: string[],
	currentHostname: string,
): string => {
	if (!locale || !locales.includes(locale)) return href;

	let targetUrl: URL;
	try {
		targetUrl = new URL(href);
	} catch {
		return href;
	}

	if (targetUrl.protocol !== "http:" && targetUrl.protocol !== "https:") return href;

	const targetTld = getTopLevelDomain(targetUrl.hostname);
	const currentTld = getTopLevelDomain(currentHostname);
	if (!targetTld || !currentTld || targetTld === currentTld) return href;

	const setLangSupported = isSetLangSupported(href);
	if (!setLangSupported) return href;

	targetUrl.searchParams.set("setlang", locale);
	return targetUrl.toString();
};
